// web-frontend/src/shared/constants/chatbotActions.js
/**
 * 챗봇 액션 상수 정의
 * 
 * ChatbotModal, MessageBubble 등에서 사용되는 액션 타입들을 정의합니다.
 * 
 * @version 1.1.0
 * @created 2026-01-24
 * @updated 2026-01-24 - SUPPORTED_LANGUAGES, getLanguageName, SUGGEST_LANG_CHANGE 추가
 */

/**
 * 지원 언어 목록
 * DSK 2026 플랫폼에서 지원하는 언어 코드 및 정보
 */
export const SUPPORTED_LANGUAGES = {
  ko: {
    code: 'ko',
    name: '한국어',
    nativeName: '한국어',
    flag: '🇰🇷',
    rtl: false
  },
  en: {
    code: 'en',
    name: 'English',
    nativeName: 'English',
    flag: '🇺🇸',
    rtl: false
  },
  ja: {
    code: 'ja',
    name: 'Japanese',
    nativeName: '日本語',
    flag: '🇯🇵',
    rtl: false
  },
  zh: {
    code: 'zh',
    name: 'Chinese',
    nativeName: '中文',
    flag: '🇨🇳',
    rtl: false
  }
};

/**
 * 언어 코드로 언어 이름 반환
 * @param {string} langCode - 언어 코드 (ko, en, ja, zh)
 * @param {boolean} [native=true] - true면 해당 언어로 이름 반환, false면 영어로 반환
 * @returns {string} - 언어 이름
 */
export const getLanguageName = (langCode, native = true) => {
  const lang = SUPPORTED_LANGUAGES[langCode];
  if (!lang) {
    return langCode; // 지원하지 않는 언어면 코드 그대로 반환
  }
  return native ? lang.nativeName : lang.name;
};

/**
 * 언어 코드로 국기 이모지 반환
 * @param {string} langCode - 언어 코드
 * @returns {string} - 국기 이모지
 */
export const getLanguageFlag = (langCode) => {
  const lang = SUPPORTED_LANGUAGES[langCode];
  return lang?.flag || '🌐';
};

/**
 * 지원하는 언어인지 확인
 * @param {string} langCode - 언어 코드
 * @returns {boolean}
 */
export const isSupportedLanguage = (langCode) => {
  return langCode in SUPPORTED_LANGUAGES;
};

/**
 * 지원 언어 목록 배열로 반환
 * @returns {Array} - 언어 정보 배열
 */
export const getSupportedLanguageList = () => {
  return Object.values(SUPPORTED_LANGUAGES);
};

/**
 * 챗봇 액션 타입 상수
 * 
 * 챗봇 응답에서 사용되는 action 필드 값들을 정의합니다.
 * 백엔드 chatbot API 응답의 action 필드와 동기화되어야 합니다.
 */
export const CHATBOT_ACTIONS = {
  // ==================== 네비게이션 관련 ====================
  /** 경로 안내 (지도에서 A→B 경로 표시) */
  NAVIGATE: 'NAVIGATE',
  
  /** 현재 위치 질문 (사용자에게 현재 위치 입력 요청) */
  ASK_LOCATION: 'ASK_LOCATION',
  
  /** 최적 경로 표시 (다중 목적지 최적 경로) */
  SHOW_OPTIMAL_ROUTE: 'SHOW_OPTIMAL_ROUTE',
  
  /** 혼잡도 정보 표시 */
  SHOW_CONGESTION: 'SHOW_CONGESTION',
  
  // ==================== 기업/부스 관련 ====================
  /** 기업 하이라이트 (지도에서 특정 부스들 강조 표시) */
  HIGHLIGHT_COMPANIES: 'HIGHLIGHT_COMPANIES',
  
  /** 지도에서 기업 표시 (HIGHLIGHT_COMPANIES와 유사) */
  SHOW_COMPANIES_ON_MAP: 'SHOW_COMPANIES_ON_MAP',
  
  /** 기업 추천 결과 */
  RECOMMEND_COMPANIES: 'RECOMMEND_COMPANIES',
  
  /** 기업 검색 결과 */
  SEARCH_RESULT: 'SEARCH_RESULT',
  
  /** 기업 상세 정보 표시 */
  SHOW_COMPANY_DETAIL: 'SHOW_COMPANY_DETAIL',
  
  // ==================== 설문조사 관련 ====================
  /** 설문조사 필수 (설문 미완료 사용자) */
  SURVEY_REQUIRED: 'SURVEY_REQUIRED',
  
  /** 설문조사 유도 (맞춤 추천을 위한 설문 권장) */
  SURVEY_PROMPT: 'SURVEY_PROMPT',
  
  // ==================== 미팅 관련 ====================
  /** 미팅 예약 모달 열기 */
  OPEN_MEETING_MODAL: 'OPEN_MEETING_MODAL',
  
  /** 미팅 일정 표시 */
  SHOW_MEETING_SCHEDULE: 'SHOW_MEETING_SCHEDULE',
  
  // ==================== 언어/설정 관련 ====================
  /** 앱 언어 변경 */
  SET_APP_LANG: 'SET_APP_LANG',
  
  /** 언어 변경 제안 (사용자에게 언어 변경 여부 묻기) */
  SUGGEST_LANG_CHANGE: 'SUGGEST_LANG_CHANGE',
  
  /** 팝업/모달 닫기 */
  CLOSE_POPUP: 'CLOSE_POPUP',
  
  // ==================== 이벤트 관련 ====================
  /** 이벤트 정보 표시 */
  SHOW_EVENT_INFO: 'SHOW_EVENT_INFO',
  
  /** 이벤트 일정 표시 */
  SHOW_EVENT_SCHEDULE: 'SHOW_EVENT_SCHEDULE',
  
  // ==================== 시설 관련 ====================
  /** 시설 위치 안내 */
  SHOW_FACILITY: 'SHOW_FACILITY',
  
  // ==================== 기타 ====================
  /** 일반 텍스트 응답 (액션 없음) */
  TEXT_RESPONSE: 'TEXT_RESPONSE',
  
  /** 에러 발생 */
  ERROR: 'ERROR',
};

/**
 * 액션 페이로드 타입 (TypeScript 참고용 주석)
 * 
 * NAVIGATE: { from: string, to: string, path_points?: array }
 * ASK_LOCATION: { destination: string, destination_company?: string, facility_type?: string }
 * HIGHLIGHT_COMPANIES: { booths: array }
 * SET_APP_LANG: { target_lang: 'ko' | 'en' | 'ja' | 'zh' }
 * SUGGEST_LANG_CHANGE: { target_lang: 'ko' | 'en' | 'ja' | 'zh', detected_lang?: string }
 * OPEN_MEETING_MODAL: { companyId: string, companyName?: string }
 * RECOMMEND_COMPANIES: { companies: array }
 * SEARCH_RESULT: { companies: array, query?: string }
 * SHOW_FACILITY: { facility_type: string, from?: string, to?: string, path?: array, path_points?: array, facilities?: array }
 */

/**
 * 액션이 지도 관련인지 확인
 * @param {string} action - 액션 타입
 * @returns {boolean}
 */
export const isMapAction = (action) => {
  return [
    CHATBOT_ACTIONS.NAVIGATE,
    CHATBOT_ACTIONS.ASK_LOCATION,
    CHATBOT_ACTIONS.SHOW_OPTIMAL_ROUTE,
    CHATBOT_ACTIONS.HIGHLIGHT_COMPANIES,
    CHATBOT_ACTIONS.SHOW_COMPANIES_ON_MAP,
    CHATBOT_ACTIONS.SHOW_FACILITY,
  ].includes(action);
};

/**
 * 액션이 기업 관련인지 확인
 * @param {string} action - 액션 타입
 * @returns {boolean}
 */
export const isCompanyAction = (action) => {
  return [
    CHATBOT_ACTIONS.HIGHLIGHT_COMPANIES,
    CHATBOT_ACTIONS.SHOW_COMPANIES_ON_MAP,
    CHATBOT_ACTIONS.RECOMMEND_COMPANIES,
    CHATBOT_ACTIONS.SEARCH_RESULT,
    CHATBOT_ACTIONS.SHOW_COMPANY_DETAIL,
  ].includes(action);
};

/**
 * 액션이 설문 관련인지 확인
 * @param {string} action - 액션 타입
 * @returns {boolean}
 */
export const isSurveyAction = (action) => {
  return [
    CHATBOT_ACTIONS.SURVEY_REQUIRED,
    CHATBOT_ACTIONS.SURVEY_PROMPT,
  ].includes(action);
};

/**
 * 액션이 언어 관련인지 확인
 * @param {string} action - 액션 타입
 * @returns {boolean}
 */
export const isLanguageAction = (action) => {
  return [
    CHATBOT_ACTIONS.SET_APP_LANG,
    CHATBOT_ACTIONS.SUGGEST_LANG_CHANGE,
  ].includes(action);
};

/**
 * 액션이 미팅 관련인지 확인
 * @param {string} action - 액션 타입
 * @returns {boolean}
 */
export const isMeetingAction = (action) => {
  return [
    CHATBOT_ACTIONS.OPEN_MEETING_MODAL,
    CHATBOT_ACTIONS.SHOW_MEETING_SCHEDULE,
  ].includes(action);
};

export default CHATBOT_ACTIONS;